import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { Controller, useForm } from 'react-hook-form'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { ILiveSearchFilters } from '../../model/types/LiveSearchFilters.interface'
import {
  selectLiveSearchApplyFilters,
  selectLiveSearchFilters,
  setLiveSearchApplyFilters,
  setLiveSearchFilter,
} from '../../model/liveSearchSlice'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { BpSearchInput } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { DeviceIcon } from '../../../../shared/components/icons/filters/DeviceIcon'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { IconRoundedCheck } from '../../../../shared/components/icons/IconRoundedCheck'
import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import styles from './LiveSearchFilters.module.scss'
import {
  GetFilterCountryApiArg,
  useGetFilterCountryQuery,
  useGetFilterDeviceTypesQuery,
  useGetFilterEngineLiveSearchQuery,
} from '../../../../app/api'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { SearchEngineIcon } from '../../../../shared/components/icons/filters/SearchEngineIcon'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'

const Filter = ({ searchParams, setSearchParams }: IWithSearchParamsProps<ILiveSearchFilters>) => {
  const { t } = useTranslation()
  const applyFilters = useAppSelector(selectLiveSearchApplyFilters)
  const { country, searchEngine, device, keyword } =
    useAppSelector<ILiveSearchFilters>(selectLiveSearchFilters)
  const dispatch = useAppDispatch()

  const {
    getValues,
    control,
    handleSubmit,
    formState: { defaultValues, isValid },
  } = useForm<ILiveSearchFilters>({
    mode: 'onChange',
    defaultValues: {
      country,
      searchEngine,
      device,
      keyword,
      ...searchParams,
    },
  })

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery({
    projectsCountriesPriority: 1,
  } as unknown as GetFilterCountryApiArg)

  const {
    data: devices,
    isFetching: devicesFetching,
    isLoading: devicesLoading,
  } = useGetFilterDeviceTypesQuery()

  const {
    data: engines,
    isFetching: enginesFetching,
    isLoading: enginesLoading,
  } = useGetFilterEngineLiveSearchQuery()

  const applyFilter = () => {
    const values = getValues()
    dispatch(setLiveSearchFilter(values))
    dispatch(setLiveSearchApplyFilters(true))
    if (setSearchParams) setSearchParams(values)
  }

  const handleChange = () => {
    const { country, searchEngine, device, keyword } = getValues()
    if (setSearchParams) setSearchParams({ country, searchEngine, device, keyword })
  }

  return (
    <form className={clsx(styles.form)} onSubmit={handleSubmit(applyFilter)}>
      <div className={clsx(styles.field)}>
        <Controller
          render={({ field: { onChange, ...fieldProps } }) => {
            const defaultValue = defaultValues?.country
              ? defaultValues.country
              : country
                ? country
                : countries && countries[0].value

            return (
              <BpSelect
                {...fieldProps}
                Icon={CountryIcon}
                defaultValue={defaultValue}
                disabled={countriesLoading || countriesFetching}
                fieldSize='lg'
                loading={countriesLoading || countriesFetching}
                optionRender={renderCountryOption}
                options={countries}
                title={t('fields.country')}
                onChange={(e) => {
                  onChange(e)
                  handleChange()
                }}
              ></BpSelect>
            )
          }}
          control={control}
          name='country'
        />
      </div>
      <div className={clsx(styles.field)}>
        <Controller
          render={({ field: { onChange, ...fieldProps } }) => {
            const defaultValue = defaultValues?.device
              ? defaultValues.device
              : device
                ? device
                : devices && devices[0].value

            return (
              <BpSelect
                {...fieldProps}
                Icon={DeviceIcon}
                defaultValue={defaultValue}
                disabled={devicesLoading || devicesFetching}
                fieldSize='lg'
                loading={devicesLoading || devicesFetching}
                options={devices}
                title={t('fields.device')}
                onChange={(e) => {
                  onChange(e)
                  handleChange()
                }}
              ></BpSelect>
            )
          }}
          control={control}
          name='device'
        />
      </div>
      <div className={clsx(styles.field)}>
        <Controller
          render={({ field: { onChange, ...fieldProps } }) => {
            const defaultValue = defaultValues?.searchEngine
              ? defaultValues.searchEngine
              : searchEngine
                ? searchEngine
                : engines && 2

            return (
              <BpSelect
                {...fieldProps}
                Icon={SearchEngineIcon}
                defaultValue={defaultValue}
                disabled={enginesLoading || enginesFetching}
                fieldSize='lg'
                loading={enginesLoading || enginesFetching}
                options={engines}
                title={t('fields.searchEngine')}
                onChange={(e) => {
                  onChange(e)
                  handleChange()
                }}
              ></BpSelect>
            )
          }}
          control={control}
          name='searchEngine'
        />
      </div>
      <div className={clsx(styles.field)}>
        <Controller
          render={({ field: { onChange, ...fieldProps } }) => (
            <BpSearchInput
              {...fieldProps}
              fieldSize='lg'
              placeholder={t('fields.keyword')}
              onChange={(e) => {
                onChange(e)
                handleChange()
              }}
            ></BpSearchInput>
          )}
          control={control}
          name='keyword'
          rules={{ required: true }}
        />
      </div>
      <div className={clsx(styles.buttonWrapper)}>
        <BpButton
          className={clsx(styles.buttonFull)}
          disabled={!isValid || applyFilters}
          iconLeft={<IconRoundedCheck />}
          size='lg'
        >
          {t('buttons.apply')}
        </BpButton>
      </div>
    </form>
  )
}

export const LiveSearchFilters = withSearchParams<
  IWithSearchParamsProps<ILiveSearchFilters>,
  ILiveSearchFilters
>(Filter, selectLiveSearchFilters)
